import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaHotel, FaUser, FaCheckCircle, FaExclamationCircle, FaImage } from "react-icons/fa";
import Swal from "sweetalert2";
import "./HotelOwners.css";

import { getFileUrl } from "../utils/imageUrl";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function HotelListingDetailsPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await fetch(`${API}/admin/listings/hotels/${listingId}`);
        const json = await res.json();
        if (res.ok) {
          setData(json);
        } else {
          setError(json.message || "Failed to load listing details");
        }
      } catch (e) {
        console.error(e);
        setError("Network error");
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, [listingId]);

  const handleActivate = async () => {
    try {
      const result = await Swal.fire({
        title: "Activate/Unhold Listing?",
        text: "This will set the listing status to Active.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Activate",
        confirmButtonColor: "#10b981",
      });

      if (!result.isConfirmed) return;

      const res = await fetch(`${API}/admin/listings/${listingId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Active" })
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Activated!", "The listing status is now Active.", "success");
        setData(prev => prev ? { ...prev, statusName: "Active" } : null);
      } else {
        throw new Error(resData.message || "Failed to activate");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const handleIssue = async () => {
    try {
      const { value: formValues } = await Swal.fire({
        title: "Report an Issue",
        html: `
          <div style="text-align: left; margin-bottom: 10px;">
            <label style="display: block; font-size: 14px; font-weight: bold; margin-bottom: 8px;">Issue Description</label>
            <textarea id="swal-issue-details" class="swal2-textarea" style="width: 100%; box-sizing: border-box; height: 100px; margin: 0; font-size: 14px; padding: 10px;" placeholder="Type the issue details here..."></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Send Email & Delete",
        confirmButtonColor: "#ef4444",
        preConfirm: () => {
          const issueDetails = document.getElementById("swal-issue-details").value;
          if (!issueDetails.trim()) {
            Swal.showValidationMessage("Please enter issue details");
            return false;
          }
          return { issueDetails };
        }
      });

      if (!formValues) return;

      const res = await fetch(`${API}/admin/listings/hotels/${listingId}/issue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });
      const resData = await res.json();

      if (res.ok) {
        Swal.fire("Deleted!", "The listing has been deleted and the owner notified.", "success");
        navigate("/admin/listings");
      } else {
        throw new Error(resData.message || "Failed to report issue");
      }
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  if (loading) return <div className="owners-page"><div className="premium-glass-card">Loading...</div></div>;
  if (error) return <div className="owners-page"><div className="premium-glass-card" style={{ color: 'crimson' }}>{error}</div></div>;
  if (!data) return <div className="owners-page"><div className="premium-glass-card">Not found.</div></div>;

  const imageUrl = (path) => getFileUrl(path, API);

  return (
    <div className="owners-page">
      <div className="owners-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <button onClick={() => navigate(-1)} className="badge" style={{ marginBottom: '10px', background: '#fff', border: '1px solid #ddd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FaArrowLeft /> Back to List
          </button>
          <h2 className="owners-title">Hotel Listing Details</h2>
          <p className="muted">Review information and photos for HL-{String(data.listingId).padStart(6, '0')}</p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {data.statusName !== "Active" && (
            <button onClick={handleActivate} className="badge" style={{ background: '#10b981', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <FaCheckCircle /> Activate Listing
            </button>
          )}
          <button onClick={handleIssue} className="badge" style={{ background: '#ef4444', color: '#fff', fontSize: '14px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <FaExclamationCircle /> Report Issue & Delete
          </button>
        </div>
      </div>

      <div className="card table-card" style={{ padding: '30px', marginTop: '20px' }}>
        <div className="detail-section">
          <h4><FaHotel /> Listing & Property Info</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Hotel Name:</label> <span>{data.hotelName || "—"}</span></div>
            <div className="detail-item"><label>Room Type:</label> <span>{data.roomTypeName || "—"}</span></div>
            <div className="detail-item"><label>Price:</label> <span>{data.price ? `${data.currency || 'USD'} ${data.price.toLocaleString()}` : "—"}</span></div>
            <div className="detail-item"><label>Location/Address:</label> <span>{data.hotelAddress || "—"}</span></div>
            <div className="detail-item"><label>Status:</label> <span className={`badge ${data.statusName === 'Active' ? 'green' : 'amber'}`}>{data.statusName || "Pending"}</span></div>
            <div className="detail-item"><label>Created At:</label> <span>{data.create_at ? new Date(data.create_at).toLocaleString() : "—"}</span></div>
          </div>
          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Description:</label>
            <p style={{ margin: 0, color: '#4b5563', lineHeight: '1.6' }}>{data.description || "No description provided."}</p>
          </div>
        </div>

        <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />

        <div className="detail-section">
          <h4><FaUser /> Owner Contact Details</h4>
          <div className="detail-grid">
            <div className="detail-item"><label>Full Name:</label> <span>{data.ownerName || "—"}</span></div>
            <div className="detail-item"><label>Email Address:</label> <span>{data.ownerEmail || "—"}</span></div>
            <div className="detail-item"><label>Phone Number:</label> <span>{data.ownerPhone || "—"}</span></div>
          </div>
        </div>

        {data.images && data.images.length > 0 && (
          <>
            <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '30px 0' }} />
            <div className="detail-section documents">
              <h4><FaImage /> Listing Gallery</h4>
              <div className="document-grid">
                {data.images.map((img, idx) => (
                  <div key={idx} className="doc-item">
                    <label>Photo {idx + 1}</label>
                    <div className="doc-preview" onClick={() => window.open(imageUrl(img), '_blank')} style={{ position: 'relative', cursor: 'pointer' }}>
                      <img
                        src={imageUrl(img)}
                        alt={`Room ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
